import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewChild
} from '@angular/core';
import { OrderLocation } from '@ml/orders/types/classes';
import { Location } from '@ml/locations/types';
import { AgmMap } from '@agm/core';
import { TaskLocationStatus } from '@ml/tasks/types/enums/task-location-status.enum';

declare const google: any;

@Component({
  selector: 'ml-order-locations-map',
  template: `
    <mat-card>
      <div
        fxLayout="row"
        fxLayoutAlign="start center"
        fxLayoutGap="8px"
        class="mat-title"
      >
        <mat-icon svgIcon="location"></mat-icon>
        <div>{{ 'fields' | translate | titlecase }}</div>
      </div>
      <agm-map
        *ngIf="orderLocations.length > 0"
        [latitude]="startLatitude"
        [longitude]="startLongitude"
        [zoomControl]="true"
        [mapDraggable]="true"
        [disableDefaultUI]="true"
        [zoom]="START_ZOOM"
        [mapTypeId]="'hybrid'"
        #agmMap
        (mapReady)="onMapReady($event)"
      >
      </agm-map>
      <h4 *ngIf="orderLocations.length === 0" class="text-center padding-16">
        {{ 'no-locations' | translate }}
      </h4>
    </mat-card>
  `,
  styleUrls: ['./order-locations-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderLocationsMapComponent {
  @Input('orderLocations') set _orderLocations(
    orderLocations: OrderLocation[]
  ) {
    if (orderLocations) {
      this.orderLocations = orderLocations;
      this.initLocations();
    }
  }

  readonly START_ZOOM = 16;
  readonly POLYGON = 'polygon';

  @ViewChild(AgmMap, { static: true }) map: AgmMap;
  drawingManager: any;
  managerOptions = {
    drawingMode: null,
    drawingControl: false,
    drawingControlOptions: {
      drawingModes: []
    },
    polygonOptions: {
      draggable: false,
      editable: false,
      strokeColor: '#1b5e20',
      strokeOpacity: 0.6,
      fillColor: '#1b5e20',
      fillOpacity: 0.35
    }
  };
  startLatitude: number;
  startLongitude: number;
  orderLocations: OrderLocation[];

  protected googleMapObjects: any[] = [];

  initLocations() {
    if (this.orderLocations.length === 0) {
      return;
    }

    this.startLatitude = (this.orderLocations[0]
      .location as Location).startPoint.lat;
    this.startLongitude = (this.orderLocations[0]
      .location as Location).startPoint.long;

    if (!this.map) {
      return;
    }

    this.googleMapObjects.forEach(mapObject => {
      mapObject.setMap(null);
    });

    this.googleMapObjects = [];

    this.drawLocations();
  }

  onMapReady(map) {
    this.map = map;
    this.drawingManager = new google.maps.drawing.DrawingManager(
      this.managerOptions
    );
    this.drawingManager.setMap(this.map);

    this.drawLocations();
  }

  private drawLocations() {
    this.orderLocations.forEach((location, index) =>
      this.drawLocation(location, index + 1)
    );
  }

  private drawLocation(orderLocation: OrderLocation, index: number) {
    const location = orderLocation.location as Location;
    const points = location.points.map(point => ({
      lat: point.lat,
      lng: point.long
    }));

    const polygon = new google.maps.Polygon({
      paths: points,
      map: this.map,
      ...this.managerOptions.polygonOptions
    });

    const markerPosition = {
      lat: location.startPoint.lat,
      lng: location.startPoint.long
    };

    const marker = new google.maps.Marker({
      label: `${index}`,
      position: markerPosition,
      map: this.map,
      fontSize: 10,
      align: 'center',
      zIndex: 15,
      icon: this.pinSymbol(this.colorForStatus(orderLocation.status))
    });

    this.googleMapObjects.push(polygon);
    this.googleMapObjects.push(marker);
  }

  private colorForStatus(status: TaskLocationStatus): string {
    switch (status) {
      case TaskLocationStatus.DONE:
        return 'green';
      case TaskLocationStatus.IN_PROGRESS:
        return '#f9a825';
      case TaskLocationStatus.NOT_STARTED:
        return '#dd1f00';
      case TaskLocationStatus.STOPPED:
        return '#4e0aa7';
      default:
        return '#dd1f00';
    }
  }

  private pinSymbol(color) {
    return {
      path:
        'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z',
      fillColor: color,
      fillOpacity: 1,
      strokeColor: '#000',
      strokeWeight: 1,
      scale: 1.4,
      labelOrigin: new google.maps.Point(0, -29)
    };
  }
}
